import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/fmp.css';
import { notify } from '../utils/notify';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
// Mes que a pagina ja abre selecionado (8 = Agosto)
const MES_PADRAO = 8;

// Codigos de todos os nos que tem filhos -> usado para abrir o DRE 100% fechado
const codigosComFilhos = (tipos) => {
  const acc = new Set();
  const walk = (n) => {
    if (Array.isArray(n.filhos) && n.filhos.length > 0) {
      acc.add(n.codigo);
      n.filhos.forEach(walk);
    }
  };
  (tipos || []).forEach(walk);
  return acc;
};

export default function Dre_horizontal() {
  const { cliente_id } = useParams();
  const navigate = useNavigate();

  const [ano, setAno] = useState(new Date().getFullYear());
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [mesIni, setMesIni] = useState(MES_PADRAO);
  const [mesFim, setMesFim] = useState(MES_PADRAO);
  const [colapsados, setColapsados] = useState(new Set());
  const [sincronizando, setSincronizando] = useState(false);
  const [baseData, setBaseData] = useState('vencimento'); // 'vencimento' | 'pagamento'
  // colunas da tabela: um pilar por coluna (padrão nos clientes com pilares) ou meses
  const [visao, setVisao] = useState('meses'); // 'meses' | 'pilares'
  // enquanto o usuário não escolher nas pílulas, a visão segue o cadastro do cliente
  const visaoManualRef = useRef(false);
  const scrollRef = useRef(null);
  const rolar = (delta) => { if (scrollRef.current) scrollRef.current.scrollBy({ left: delta, behavior: 'smooth' }); };
  // Lançamentos inline por categoria
  const [expandidas, setExpandidas] = useState({});
  const [lancamentos, setLancamentos] = useState({});
  const [carregLanc, setCarregLanc] = useState({});

  // Filtro GERAL de status (topo) — ref evita closure desatualizada no buscar
  // Padrão: 'pago' (só realizados) — o mesmo do DRE vertical, para os dois baterem ao abrir
  const filtroLancRef = useRef('pago');
  // Consolidar empresas do grupo (25 + 27): soma os dois DREs em um só
  const [consolidado, setConsolidado] = useState(false);
  const consolidadoRef = useRef(false);
  // ao entrar (ou trocar cliente/ano/base), o DRE vem todo fechado
  const colapsarTudoRef = useRef(true);

  const buscar = useCallback(async (mi, mf, st) => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('access');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/dre-horizontal/`,
        { params: { cliente_id, ano, mes_ini: mi, mes_fim: mf, base_data: baseData, status: st || filtroLancRef.current,
                    ...(consolidadoRef.current ? { consolidar: 1 } : {}) },
          headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.data.success) {
        setDados(res.data);
        // cliente com pilares abre direto na visão por pilar (uma coluna por pilar)
        if (!visaoManualRef.current) {
          setVisao((res.data.departamentos || []).length > 0 ? 'pilares' : 'meses');
        }
        if (colapsarTudoRef.current) {
          setColapsados(codigosComFilhos(res.data.tipos));
          colapsarTudoRef.current = false;
        }
      }
      else setDados({ erro: res.data.erro || 'Falha ao gerar DRE.' });
    } catch (e) {
      console.error('Erro ao carregar DRE horizontal:', e);
      setDados({ erro: e.response?.data?.erro || 'Erro ao carregar DRE horizontal.' });
    } finally {
      setCarregando(false);
    }
  }, [cliente_id, ano, baseData]);

  useEffect(() => {
    if (!cliente_id) return;
    setMesIni(MES_PADRAO);
    setMesFim(MES_PADRAO);
    colapsarTudoRef.current = true;
    visaoManualRef.current = false;
    setExpandidas({});
    setLancamentos({});
    buscar(MES_PADRAO, MES_PADRAO);
  }, [cliente_id, ano, buscar]);

  const sincronizarEstrutura = async () => {
    setSincronizando(true);
    try {
      const token = localStorage.getItem('access');
      const res = await axios.post(`${process.env.REACT_APP_API_BASE}/api/sincronizar-estrutura-dre/`,
        { cliente_id }, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.data.success) {
        await buscar(mesIni, mesFim);
        notify.sucesso(`Estrutura DRE sincronizada (${res.data.categorias_mapeadas} categorias).`);
      } else {
        notify.erro(res.data.erro || 'Falha ao sincronizar estrutura.');
      }
    } catch (e) {
      notify.erro(e.response?.data?.erro || 'Erro ao sincronizar estrutura DRE.');
    } finally {
      setSincronizando(false);
    }
  };

  const toggleColapso = (codigo) => {
    setColapsados(prev => {
      const next = new Set(prev);
      next.has(codigo) ? next.delete(codigo) : next.add(codigo);
      return next;
    });
  };

  // `departamento` vem do nó quando o DRE está em pilares (detalhe = fatia rateada).
  // `origens` vem do DRE CONSOLIDADO: a mesma categoria pode existir nas duas
  // empresas, com códigos diferentes — busca os lançamentos de cada uma e junta.
  const toggleLancamentos = async (codigo, departamento, origens) => {
    const key = departamento ? `${departamento}||${codigo}` : codigo;
    if (expandidas[key]) {
      setExpandidas(prev => ({ ...prev, [key]: false }));
      return;
    }
    setExpandidas(prev => ({ ...prev, [key]: true }));
    if (lancamentos[key]) return;
    setCarregLanc(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('access');
      const nomes = {};
      (dados?.clientes || []).forEach(cl => { nomes[String(cl.id)] = cl.nome; });
      const alvos = (origens && origens.length)
        ? origens
        : [{ cliente_id, codigo, departamento }];
      const listas = await Promise.all(alvos.map(async (o) => {
        const params = { cliente_id: o.cliente_id ?? cliente_id, ano, mes_ini: mesIni, mes_fim: mesFim,
                         codigo_categoria: o.codigo ?? codigo, base_data: baseData, status: filtroLancRef.current };
        if (o.departamento) params.departamento = o.departamento;
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE}/api/dre-horizontal-lancamentos/`,
          { params, headers: { 'Authorization': `Bearer ${token}` } }
        );
        const itens = res.data.success ? (res.data.lancamentos || []) : [];
        const empresa = alvos.length > 1 ? (nomes[String(o.cliente_id)] || null) : null;
        return empresa ? itens.map(l => ({ ...l, empresa })) : itens;
      }));
      const todos = [].concat(...listas).sort((x, y) => String(x.data).localeCompare(String(y.data)));
      setLancamentos(prev => ({ ...prev, [key]: todos }));
    } catch (e) {
      console.error('Erro ao buscar lançamentos:', e);
    } finally {
      setCarregLanc(prev => ({ ...prev, [key]: false }));
    }
  };

  const cellVal = (v) => {
    if (!v) return null;
    return <span style={{ color: v < 0 ? '#c62828' : '#1b5e20' }}>{formatarValor(v)}</span>;
  };

  const estilosNivel = [
    { bg: '#e8ecf2', fw: 700, cor: '#00205b' },
    { bg: '#eef1f5', fw: 600, cor: '#243b6b' },
    { bg: '#f6f8fa', fw: 600, cor: '#34495e' },
    { bg: '#ffffff', fw: 400, cor: '#2c3e50' },
  ];

  // Filtro GERAL de status (pílulas no topo) — filtra o DRE inteiro no backend
  const [filtroLanc, setFiltroLanc] = useState('pago');
  const FILTROS_LANC = [
    { key: 'todos', rotulo: 'Todos' },
    { key: 'pago', rotulo: 'Pago' },
    { key: 'aberto', rotulo: 'Aberto' },
    { key: 'vencido', rotulo: 'Vencido' },
  ];
  const aplicarFiltroLanc = (k) => {
    setFiltroLanc(k);
    filtroLancRef.current = k;
    setExpandidas({});
    setLancamentos({});
    buscar(mesIni, mesFim, k);
  };
  const pillLancStyle = (ativo) => ({
    border: '1px solid ' + (ativo ? '#00205b' : '#d4e2fb'),
    backgroundColor: ativo ? '#00205b' : '#fff',
    color: ativo ? '#fff' : '#00205b',
    borderRadius: '999px', padding: '2px 12px', fontWeight: 600, fontSize: '0.75rem',
  });

  const anoAtual = new Date().getFullYear();
  const anosOpcoes = [anoAtual + 1, anoAtual, anoAtual - 1, anoAtual - 2];

  // achata a árvore respeitando colapso, carregando o nome do pilar (nível 0)
  const linhas = [];
  if (dados && dados.tipos) {
    const walk = (node, nivel) => {
      const temFilhos = Array.isArray(node.filhos) && node.filhos.length > 0;
      linhas.push({ node, nivel, temFilhos });
      if (temFilhos && !colapsados.has(node.codigo)) {
        node.filhos.forEach(f => walk(f, nivel + 1));
      }
    };
    dados.tipos.forEach(t => walk(t, 0));
  }
  // DRE com pilares de departamento: layout enxuto (TIPO | CATEGORIA | DEPARTAMENTO).
  // Sem pilares a hierarquia tem 4 níveis e não há departamento — mantém o layout antigo.
  // Vem do CADASTRO (não das linhas visíveis): o DRE abre fechado e o layout não pode piscar.
  const comPilares = (dados?.departamentos || []).length > 0;
  const meses = dados?.meses || [];
  const labels = dados?.meses_labels || [];

  // ---- visão por pilar: uma coluna por departamento, linhas = Grupo > Categoria ----
  const modoPilar = comPilares && visao === 'pilares';
  // colunas: primeiro o cadastro (inclui pilares zerados), depois o que só existe
  // nos dados (ex.: "Sem departamento"), que vai pro fim
  const pilares = [];
  if (comPilares) {
    const vistos = new Set();
    (dados.departamentos || []).forEach(d => {
      if (d.nome && !vistos.has(d.nome)) { vistos.add(d.nome); pilares.push(d.nome); }
    });
    const extras = [];
    const coletar = (n) => {
      if (Array.isArray(n.filhos) && n.filhos.length > 0) n.filhos.forEach(coletar);
      else if (n.label && !vistos.has(n.label)) { vistos.add(n.label); extras.push(n.label); }
    };
    (dados.tipos || []).forEach(coletar);
    pilares.push(...extras.sort((a, b) => a.localeCompare(b, 'pt-BR')));
  }
  // total de um nó (qualquer nível) por pilar: soma as folhas de departamento
  const porPilar = (node) => {
    const m = {};
    const walkP = (n) => {
      if (Array.isArray(n.filhos) && n.filhos.length > 0) n.filhos.forEach(walkP);
      else m[n.label] = round2((m[n.label] || 0) + (n.total || 0));
    };
    walkP(node);
    return m;
  };
  // drill-down da categoria inteira: um alvo por departamento (e por empresa, se consolidado)
  const alvosDaCategoria = (node) => {
    const out = [];
    const walkA = (n) => {
      if (Array.isArray(n.filhos) && n.filhos.length > 0) n.filhos.forEach(walkA);
      else if (n.origens && n.origens.length) out.push(...n.origens);
      else out.push({ cliente_id, codigo: n.codigo, departamento: n.departamento });
    };
    walkA(node);
    return out;
  };
  // no modo pilar a árvore para na categoria — o departamento virou coluna
  const linhasPilar = [];
  if (modoPilar) {
    (dados.tipos || []).forEach(g => {
      const cats = Array.isArray(g.filhos) ? g.filhos : [];
      linhasPilar.push({ node: g, nivel: 0, temFilhos: cats.length > 0 });
      if (cats.length > 0 && !colapsados.has(g.codigo)) {
        cats.forEach(c => linhasPilar.push({ node: c, nivel: 1, temFilhos: false }));
      }
    });
  }
  const totaisPilar = {};
  if (modoPilar) {
    (dados.tipos || []).forEach(g => {
      const m = porPilar(g);
      Object.keys(m).forEach(k => { totaisPilar[k] = round2((totaisPilar[k] || 0) + m[k]); });
    });
  }
  const linhasRender = modoPilar ? linhasPilar : linhas;
  const nColsRotulo = modoPilar ? 2 : (comPilares ? 3 : 4);
  const nColsValor = modoPilar ? pilares.length : meses.length;

  return (
    <div className="das-page fmp-brand-page">
      <div className="container-fluid mt-4 mb-5">
        {/* Cabeçalho */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-secondary btn-sm" title="Voltar ao Dashboard"
              onClick={async () => {
                // dashboard certo depende do SISTEMA do cliente (Omie × Conta Azul)
                let rota = `/dashboard-cliente/${cliente_id}`;
                try {
                  const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } });
                  if ((r.data?.cliente?.sistema || '').toLowerCase() === 'conta_azul') rota = `/dashboard-conta-azul/${cliente_id}`;
                } catch (e) { /* mantém a rota padrão */ }
                navigate(rota);
              }}>
              <i className="fas fa-arrow-left me-1"></i>Voltar ao Dashboard
            </button>
            <div className="d-flex align-items-center">
              <i className="fas fa-table-cells header-icon me-2"></i>
              <h2 className="titulo-pagina m-0">DRE Horizontal — {ano}</h2>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {dados?.sistema === 'conta_azul' && (
              <button className="btn btn-outline-primary btn-sm" onClick={sincronizarEstrutura} disabled={sincronizando}
                title="Buscar grupos/subgrupos do DRE direto da API do Conta Azul">
                {sincronizando
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Sincronizando...</>
                  : <><i className="fas fa-sync me-1"></i>Sincronizar estrutura DRE</>}
              </button>
            )}
            <label className="text-muted small mb-0">Ano:</label>
            <select className="form-select form-select-sm" style={{ width: '110px' }} value={ano} onChange={e => setAno(parseInt(e.target.value))}>
              {anosOpcoes.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {/* Banner do critério + seletor de base de data */}
        <div style={{ background: '#e7f0fb', border: '1px solid #b6d4f2', borderRadius: '8px', padding: '10px 18px', textAlign: 'center', fontSize: '15px', fontWeight: 700, color: '#00205b', letterSpacing: '0.2px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>
            <i className="fas fa-calendar-check me-2"></i>
            Competência por <span style={{ textDecoration: 'underline' }}>{baseData === 'pagamento' ? 'DATA DE PAGAMENTO' : 'DATA DE VENCIMENTO'}</span>
          </span>
          {dados?.sistema === 'conta_azul' && (
            <span style={{ display: 'inline-flex', gap: '4px' }}>
              {[['vencimento', 'Vencimento'], ['pagamento', 'Pagamento']].map(([val, lbl]) => (
                <button key={val} onClick={() => setBaseData(val)}
                  style={{ border: 'none', borderRadius: '16px', padding: '4px 12px', fontSize: '13px', cursor: 'pointer',
                    fontWeight: baseData === val ? 700 : 500,
                    backgroundColor: baseData === val ? '#00205b' : '#cfe0f5',
                    color: baseData === val ? '#fff' : '#00205b' }}>
                  {lbl}
                </button>
              ))}
            </span>
          )}
        </div>

        {/* Filtro de meses */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #e9ecef', borderRadius: '8px', padding: '12px 18px', marginBottom: '14px' }}>
          <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
            <i className="fas fa-calendar-alt" style={{ color: '#adb5bd', fontSize: '15px' }}></i>
            {MESES_ABREV.map((m, i) => {
              const mes = i + 1;
              const ativo = mes >= mesIni && mes <= mesFim;
              return (
                <button
                  key={mes}
                  onClick={() => {
                    let ini = mesIni, fim = mesFim;
                    if (mes < mesIni) { ini = mes; }
                    else if (mes > mesFim) { fim = mes; }
                    else if (mes === mesIni && mes !== mesFim) { ini = mes + 1; }
                    else if (mes === mesFim && mes !== mesIni) { fim = mes - 1; }
                    else { ini = mes; fim = mes; }
                    setMesIni(ini);
                    setMesFim(fim);
                    // limpa lançamentos expandidos: eram do período anterior
                    // (cache por categoria não considera o mês selecionado)
                    setExpandidas({});
                    setLancamentos({});
                    buscar(ini, fim);
                  }}
                  style={{
                    border: 'none', borderRadius: '20px', padding: '6px 14px', fontSize: '14px',
                    fontWeight: ativo ? 700 : 400, cursor: 'pointer',
                    backgroundColor: ativo ? '#00205b' : '#f1f3f5', color: ativo ? '#fff' : '#868e96',
                    transition: 'all 0.15s', minWidth: '46px',
                  }}
                >{m}</button>
              );
            })}
            <span style={{ width: 1, height: 22, background: '#dbe3ef', margin: '0 6px' }}></span>
            <small style={{ color: '#7d8aa3', fontWeight: 600 }}>Status:</small>
            {FILTROS_LANC.map((f) => (
              <button key={f.key} type="button" style={pillLancStyle(filtroLanc === f.key)}
                onClick={() => aplicarFiltroLanc(f.key)}>{f.rotulo}</button>
            ))}
            {comPilares && (
              <>
                <span style={{ width: 1, height: 22, background: '#dbe3ef', margin: '0 6px' }}></span>
                <small style={{ color: '#7d8aa3', fontWeight: 600 }}>Colunas:</small>
                {[['meses', 'Meses'], ['pilares', 'Pilares']].map(([v, lbl]) => (
                  <button key={v} type="button" style={pillLancStyle(visao === v)}
                    title={v === 'pilares'
                      ? 'Uma coluna por pilar, somando o período selecionado'
                      : 'Uma coluna por mês do período'}
                    onClick={() => { visaoManualRef.current = true; setVisao(v); setExpandidas({}); }}>{lbl}</button>
                ))}
              </>
            )}
            {(dados?.consolidavel || consolidado) && (
              <>
                <span style={{ width: 1, height: 22, background: '#dbe3ef', margin: '0 6px' }}></span>
                <button type="button"
                  title="Somar este DRE com o da outra empresa do grupo (Advantec + Advwg)"
                  style={{ border: '1px solid ' + (consolidado ? '#b8860b' : '#d4e2fb'),
                           backgroundColor: consolidado ? '#b8860b' : '#fff',
                           color: consolidado ? '#fff' : '#00205b',
                           borderRadius: '999px', padding: '2px 12px', fontWeight: 600, fontSize: '0.75rem' }}
                  onClick={() => {
                    const v = !consolidado;
                    setConsolidado(v); consolidadoRef.current = v;
                    setExpandidas({}); setLancamentos({});
                    buscar(mesIni, mesFim);
                  }}>
                  <i className="fas fa-layer-group me-1"></i>Consolidar empresas
                </button>
                {dados?.consolidado && (
                  <span className="badge" style={{ backgroundColor: '#fff8e1', color: '#7a5b00', fontWeight: 600 }}>
                    {(dados.clientes || []).map(cl => cl.nome).join(' + ')}
                  </span>
                )}
              </>
            )}
            {carregando && <span className="spinner-border spinner-border-sm ms-2" style={{ color: '#00205b', width: '16px', height: '16px' }}></span>}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
              <button onClick={() => rolar(-500)} title="Rolar para a esquerda"
                style={{ border: '1px solid #00205b', background: '#fff', color: '#00205b', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '16px', fontWeight: 700, lineHeight: 1 }}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <button onClick={() => rolar(500)} title="Rolar para a direita"
                style={{ border: '1px solid #00205b', background: '#fff', color: '#00205b', borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '16px', fontWeight: 700, lineHeight: 1 }}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </span>
          </div>
        </div>

        {/* Tabela */}
        <div className="card border-0 shadow-sm" style={{ overflow: 'hidden' }}>
          {carregando && !dados ? (
            <div className="text-center py-5"><span className="spinner-border text-primary"></span><p className="text-muted mt-2">Gerando DRE...</p></div>
          ) : !dados ? null : dados.erro ? (
            <div className="alert alert-warning m-4 mb-0">{dados.erro}</div>
          ) : (dados.tipos || []).length === 0 ? (
            <div className="text-center py-5 text-muted"><i className="fas fa-inbox fa-3x mb-3 d-block"></i>Sem movimentos no período.</div>
          ) : (
            <div ref={scrollRef} style={{ overflowX: 'auto' }}>
              <table className="mb-0" style={{ borderCollapse: 'collapse', width: 'max-content', fontSize: '16px', whiteSpace: 'nowrap' }}>
                <thead>
                  <tr style={{ backgroundColor: '#00205b', color: '#fff', fontSize: '14px', letterSpacing: '0.4px' }}>
                    {/* com pilares o nível 0 é o GRUPO do DRE ("1.1 RECEITA BRUTA"), precisa de largura */}
                    {/* sem `width` fixo: a coluna cresce até caber o maior rótulo em UMA linha */}
                    <th style={{ padding: '14px 8px', textAlign: 'left',
                                 minWidth: comPilares ? '260px' : '40px', position: 'sticky', left: 0, backgroundColor: '#00205b' }}>
                      {comPilares ? 'GRUPO' : 'TIPO'}
                    </th>
                    {modoPilar ? (
                      <th style={{ padding: '14px 8px', textAlign: 'left', minWidth: '300px' }}>CATEGORIA</th>
                    ) : comPilares ? (
                      <>
                        <th style={{ padding: '14px 8px', textAlign: 'left', width: '240px', minWidth: '240px' }}>CATEGORIA</th>
                        <th style={{ padding: '14px 8px', textAlign: 'left', width: '190px', minWidth: '190px' }}>DEPARTAMENTO</th>
                      </>
                    ) : (
                      <>
                        <th style={{ padding: '14px 8px', textAlign: 'left', width: '64px', minWidth: '64px' }}>GRUPO</th>
                        <th style={{ padding: '14px 8px', textAlign: 'left', width: '80px', minWidth: '80px' }}>CONTA DO DRE</th>
                        <th style={{ padding: '14px 8px', textAlign: 'left', width: '110px', minWidth: '110px' }}>CATEGORIA</th>
                      </>
                    )}
                    {modoPilar
                      ? pilares.map((pl, i) => (
                          <th key={i} style={{ padding: '14px 16px', textAlign: 'right', minWidth: '150px',
                                               whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{pl}</th>
                        ))
                      : labels.map((l, i) => <th key={i} style={{ padding: '14px 16px', textAlign: 'right', minWidth: '125px' }}>{l}</th>)}
                    <th style={{ padding: '14px 16px', textAlign: 'right', minWidth: '135px', borderLeft: '2px solid rgba(255,255,255,.25)' }}>Totais</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ height: '16px' }}><td colSpan={nColsRotulo + 1 + nColsValor} style={{ border: 'none', background: '#fff' }}></td></tr>
                  {linhasRender.map(({ node, nivel, temFilhos }, idx) => {
                    const ehFolha = !temFilhos;
                    // com pilares: folha na coluna CATEGORIA (1) e o resto da árvore em TIPO (0);
                    // sem pilares: folha na coluna CATEGORIA (3) e grupos no seu nível
                    // Grupo (0) > Categoria (1) > Departamento (folha, 2): cada nível
                    // cai na sua própria coluna. Sem pilares, mantém o layout de 4.
                    const colLabel = modoPilar
                      ? nivel
                      : comPilares
                        ? Math.min(nivel, 2)
                        : (ehFolha ? 3 : Math.min(nivel, 2));
                    const est = estilosNivel[modoPilar
                      ? (nivel === 0 ? 0 : 3)
                      : comPilares ? (ehFolha ? 3 : Math.min(nivel, 1)) : colLabel];
                    const colapsado = colapsados.has(node.codigo);
                    const cols = new Array(nColsRotulo).fill('');
                    cols[colLabel] = node.label;
                    const valPilar = modoPilar ? porPilar(node) : null;
                    // com pilares, a mesma categoria aparece em vários departamentos:
                    // a chave do detalhe precisa incluir o departamento
                    const lancKey = modoPilar
                      ? `pilar||${node.codigo}`
                      : node.departamento ? `${node.departamento}||${node.codigo}` : node.codigo;
                    const lancAberto = ehFolha && expandidas[lancKey];
                    return (
                      <React.Fragment key={lancKey + '_' + idx}>
                        <tr
                          style={{ backgroundColor: est.bg, cursor: 'pointer', borderBottom: '1px solid #eceef1' }}
                          onClick={temFilhos
                            ? () => toggleColapso(node.codigo)
                            : modoPilar
                              ? () => toggleLancamentos(node.codigo, 'pilar', alvosDaCategoria(node))
                              : () => toggleLancamentos(node.codigo, node.departamento, node.origens)}
                          title={ehFolha ? 'Ver lançamentos desta categoria' : undefined}>
                          {cols.map((c, ci) => (
                            <td key={ci} style={{
                              padding: '9px 8px',
                              fontWeight: ci === colLabel ? est.fw : 400,
                              color: est.cor,
                              paddingLeft: ci === colLabel ? `${8 + (comPilares ? nivel : colLabel) * 12}px` : '8px',
                              position: ci === 0 ? 'sticky' : undefined,
                              left: ci === 0 ? 0 : undefined,
                              backgroundColor: ci === 0 ? est.bg : undefined,
                            }}>
                              {ci === colLabel && c ? (
                                <>
                                  {temFilhos
                                    ? <i className={`fas fa-chevron-${colapsado ? 'right' : 'down'} me-2`} style={{ fontSize: '12px', opacity: .5 }}></i>
                                    : <i className={`fas fa-chevron-${lancAberto ? 'down' : 'right'} me-2`} style={{ fontSize: '11px', opacity: .35 }}></i>}
                                  {c}
                                </>
                              ) : ''}
                            </td>
                          ))}
                          {modoPilar
                            ? pilares.map((pl) => (
                                <td key={pl} style={{ padding: '9px 16px', textAlign: 'right', fontWeight: temFilhos ? 600 : 400 }}>
                                  {cellVal(valPilar[pl])}
                                </td>
                              ))
                            : meses.map((m) => (
                                <td key={m} style={{ padding: '9px 16px', textAlign: 'right', fontWeight: ehFolha ? 400 : 600 }}>
                                  {cellVal(node.valores[String(m)])}
                                </td>
                              ))}
                          <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 700, borderLeft: '2px solid #e3e6ea' }}>
                            {cellVal(node.total)}
                          </td>
                        </tr>
                        {lancAberto && (
                          <tr>
                            <td colSpan={nColsRotulo + 1 + nColsValor} style={{ padding: 0, background: '#f7f9fc', borderLeft: '4px solid #00205b' }}>
                              {carregLanc[lancKey] ? (
                                <div className="text-center py-2"><span className="spinner-border spinner-border-sm" style={{ color: '#00205b' }}></span></div>
                              ) : (() => {
                                const itensLanc = (lancamentos[lancKey] || []);
                                return (
                                <>
                                <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ backgroundColor: '#e7f0fb', color: '#00205b' }}>
                                      <th className="px-4 py-1 text-start" style={{ fontWeight: 600 }}>Vencimento</th>
                                      <th className="px-3 py-1 text-start" style={{ fontWeight: 600 }}>Fornecedor / Cliente / Descrição</th>
                                      <th className="px-3 py-1 text-start" style={{ fontWeight: 600 }}>Documento</th>
                                      <th className="px-3 py-1 text-start" style={{ fontWeight: 600 }}>Status</th>
                                      <th className="text-end px-4 py-1" style={{ fontWeight: 600 }}>Valor</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {itensLanc.length === 0 ? (
                                      <tr><td colSpan={5} className="text-center py-2" style={{ color: '#888' }}>
                                        {filtroLanc !== 'todos' ? 'Nenhum lançamento com esse status no período.' : 'Nenhum lançamento no período.'}
                                      </td></tr>
                                    ) : itensLanc.map((lc, li) => (
                                      <tr key={li} style={{ borderBottom: '1px solid #eef1f5' }}>
                                        <td className="px-4 py-1">{lc.data ? new Date(lc.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                                        <td className="px-3 py-1">
                                          {lc.empresa && <span className="badge bg-light text-dark me-1" style={{ fontWeight: 500 }}>{lc.empresa}</span>}
                                          {lc.nome || '—'}
                                        </td>
                                        <td className="px-3 py-1 text-muted">{lc.numero_documento || '—'}</td>
                                        <td className="px-3 py-1"><span className="badge bg-light text-dark" style={{ fontWeight: 500 }}>{lc.status || '—'}</span></td>
                                        <td className="text-end px-4 py-1 fw-bold" style={{ color: lc.valor < 0 ? '#c62828' : '#1b5e20' }}>{formatarValor(lc.valor)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                </>
                                );
                              })()}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {dados && dados.tipos.length > 0 && (
                    <tr style={{ backgroundColor: '#00205b', color: '#fff', fontWeight: 700, fontSize: '16px' }}>
                      <td colSpan={nColsRotulo} style={{ padding: '13px 16px', position: 'sticky', left: 0, backgroundColor: '#00205b' }}>RESULTADO LÍQUIDO</td>
                      {modoPilar
                        ? pilares.map(pl => (
                            <td key={pl} style={{ padding: '13px 16px', textAlign: 'right' }}>
                              {totaisPilar[pl] ? formatarValor(totaisPilar[pl]) : ''}
                            </td>
                          ))
                        : meses.map(m => (
                            <td key={m} style={{ padding: '13px 16px', textAlign: 'right' }}>
                              {dados.totais_mes[String(m)] ? formatarValor(dados.totais_mes[String(m)]) : ''}
                            </td>
                          ))}
                      <td style={{ padding: '13px 16px', textAlign: 'right', borderLeft: '2px solid rgba(255,255,255,.25)' }}>
                        {formatarValor(dados.total_geral)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
